import { useState, useEffect, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { useAuthStore, API_BASE, apiFetch } from '../store/auth'
import { Card, PageHeader, FormSection, FormActions } from '../components'
import { Button, PageLoading, ErrorMessage } from '../components/ui/Button'
import { Toggle } from '../components/ui/Toggle'

interface NotificationPreferences {
  transaction_created: boolean
  transaction_verified: boolean
  invoice_paid: boolean
  member_joined: boolean
  low_balance: boolean
  low_balance_threshold: number
  email_enabled: boolean
  digest_frequency: 'immediate' | 'daily' | 'weekly'
}

const DEFAULT_PREFERENCES: NotificationPreferences = {
  transaction_created: true,
  transaction_verified: false,
  invoice_paid: true,
  member_joined: true,
  low_balance: true,
  low_balance_threshold: 1000,
  email_enabled: true,
  digest_frequency: 'immediate'
}

const notificationTypes = [
  {
    key: 'transaction_created' as const,
    label: 'Uudet tapahtumat',
    description: 'Ilmoitus kun uusi tapahtuma lisätään',
    icon: '📝'
  },
  {
    key: 'transaction_verified' as const,
    label: 'Tositteet vahvistettu',
    description: 'Ilmoitus kun tosite vahvistetaan',
    icon: '✅'
  },
  {
    key: 'invoice_paid' as const,
    label: 'Laskut maksettu',
    description: 'Ilmoitus kun lasku on maksettu',
    icon: '💵'
  },
  {
    key: 'member_joined' as const,
    label: 'Uudet jäsenet',
    description: 'Ilmoitus kun uusi jäsen liittyy',
    icon: '👥'
  },
  {
    key: 'low_balance' as const,
    label: 'Budjetti-varoitukset',
    description: 'Ilmoitus kun budjetti lähestyy rajaa',
    icon: '💰'
  }
]

const digestOptions = [
  { value: 'immediate', label: 'Välittömästi', description: 'Lähetä ilmoitus heti' },
  { value: 'daily', label: 'Päivittäinen yhteenveto', description: 'Yksi sähköposti päivässä' },
  { value: 'weekly', label: 'Viikottainen yhteenveto', description: 'Yksi sähköposti viikossa' }
]

export default function NotificationSettingsPage() {
  const { t } = useTranslation()
  const { user } = useAuthStore()
  const [preferences, setPreferences] = useState<NotificationPreferences>(DEFAULT_PREFERENCES)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [successMessage, setSuccessMessage] = useState<string | null>(null)

  // Auto-dismiss error after 5 seconds
  useEffect(() => {
    if (error) {
      const timer = setTimeout(() => setError(null), 5000)
      return () => clearTimeout(timer)
    }
  }, [error])

  // Fetch preferences
  const fetchPreferences = useCallback(async () => {
    if (!user) return
    
    setLoading(true)
    setError(null)
    
    try {
      const response = await apiFetch(`${API_BASE}/notifications/preferences`)
      
      if (response.ok) {
        const data = await response.json()
        // Handle both single org and multiple orgs response
        const prefs = data.preferences
        if (Array.isArray(prefs) && prefs.length > 0) {
          setPreferences({
            transaction_created: Boolean(prefs[0].transaction_created),
            transaction_verified: Boolean(prefs[0].transaction_verified),
            invoice_paid: Boolean(prefs[0].invoice_paid),
            member_joined: Boolean(prefs[0].member_joined),
            low_balance: Boolean(prefs[0].low_balance),
            low_balance_threshold: prefs[0].low_balance_threshold || 1000,
            email_enabled: Boolean(prefs[0].email_enabled),
            digest_frequency: prefs[0].digest_frequency || 'immediate'
          })
        } else if (prefs && !Array.isArray(prefs)) {
          setPreferences({
            transaction_created: Boolean(prefs.transaction_created),
            transaction_verified: Boolean(prefs.transaction_verified),
            invoice_paid: Boolean(prefs.invoice_paid),
            member_joined: Boolean(prefs.member_joined),
            low_balance: Boolean(prefs.low_balance),
            low_balance_threshold: prefs.low_balance_threshold || 1000,
            email_enabled: Boolean(prefs.email_enabled),
            digest_frequency: prefs.digest_frequency || 'immediate'
          })
        }
      } else {
        setError('Asetusten lataaminen epäonnistui')
      }
    } catch (err) {
      setError('Asetusten lataaminen epäonnistui')
      console.error('Failed to fetch preferences:', err)
    } finally {
      setLoading(false)
    }
  }, [user])

  useEffect(() => {
    fetchPreferences()
  }, [fetchPreferences])

  // Save preferences
  const savePreferences = async () => {
    if (!user) return
    
    setSaving(true)
    setError(null)
    setSuccessMessage(null)
    
    try {
      const response = await apiFetch(`${API_BASE}/notifications/preferences`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(preferences)
      })
      
      if (response.ok) {
        setSuccessMessage('Asetukset tallennettu onnistuneesti')
        setTimeout(() => setSuccessMessage(null), 3000)
      } else {
        const data = await response.json()
        setError(data.error || 'Asetusten tallentaminen epäonnistui')
      }
    } catch (err) {
      setError('Asetusten tallentaminen epäonnistui')
      console.error('Failed to save preferences:', err)
    } finally {
      setSaving(false)
    }
  }

  // Toggle handler
  const togglePreference = (key: keyof NotificationPreferences) => {
    setPreferences(prev => ({
      ...prev,
      [key]: !prev[key]
    }))
  }

  // Threshold handler
  const handleThresholdChange = (value: string) => {
    const numValue = parseInt(value, 10)
    if (!isNaN(numValue) && numValue >= 0) {
      setPreferences(prev => ({
        ...prev,
        low_balance_threshold: numValue
      }))
    }
  }

  if (loading) {
    return (
      <div className="p-6">
        <PageLoading />
      </div>
    )
  }

  return (
    <div className="p-6 max-w-4xl mx-auto">
      <PageHeader
        title="Ilmoitusasetukset"
        subtitle="Hallitse miten ja milloin saat ilmoituksia"
      />

      {error && (
        <ErrorMessage
          message={error}
          className="mb-6"
        />
      )}

      {successMessage && (
        <div className="mb-6 p-4 bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-300 rounded-lg">
          {successMessage}
        </div>
      )}

      {/* Email Settings */}
      <Card className="mb-6">
        <FormSection
          title="Sähköposti-ilmoitukset"
          description="Valitse haluatko ilmoitukset sähköpostiin"
        >
          <div className="flex items-center justify-between py-4">
            <div>
              <h3 className="font-medium text-slate-900 dark:text-white">
                Sähköposti-ilmoitukset käytössä
              </h3>
              <p className="text-sm text-slate-500 dark:text-slate-400">
                Lähetä ilmoitukset myös sähköpostiin
              </p>
            </div>
            <Toggle
              checked={preferences.email_enabled}
              onChange={() => togglePreference('email_enabled')}
            />
          </div>

          {preferences.email_enabled && (
            <div className="mt-4 pt-4 border-t border-slate-200 dark:border-slate-700">
              <h4 className="text-sm font-medium text-slate-700 dark:text-slate-300 mb-3">
                Yhteenvetojen tiheys
              </h4>
              <div className="space-y-3">
                {digestOptions.map(option => (
                  <label
                    key={option.value}
                    className={`
                      flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition
                      ${preferences.digest_frequency === option.value
                        ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                        : 'border-slate-200 dark:border-slate-700 hover:border-slate-300'
                      }
                    `}
                  >
                    <input
                      type="radio"
                      name="digest_frequency"
                      value={option.value}
                      checked={preferences.digest_frequency === option.value}
                      onChange={(e) => setPreferences(prev => ({
                        ...prev,
                        digest_frequency: e.target.value as 'immediate' | 'daily' | 'weekly'
                      }))}
                      className="mt-1"
                    />
                    <div>
                      <div className="font-medium text-slate-900 dark:text-white">
                        {option.label}
                      </div>
                      <div className="text-sm text-slate-500 dark:text-slate-400">
                        {option.description}
                      </div>
                    </div>
                  </label>
                ))}
              </div>
            </div>
          )}
        </FormSection>
      </Card>

      {/* Notification Types */}
      <Card className="mb-6">
        <FormSection
          title="Ilmoitustyypit"
          description="Valitse mistä tapahtumista haluat ilmoituksen"
        >
          <div className="space-y-4">
            {notificationTypes.map(type => (
              <div
                key={type.key}
                className="flex items-center justify-between py-3 border-b border-slate-100 dark:border-slate-700 last:border-0"
              >
                <div className="flex items-center gap-3">
                  <span className="text-2xl">{type.icon}</span>
                  <div>
                    <h3 className="font-medium text-slate-900 dark:text-white">
                      {type.label}
                    </h3>
                    <p className="text-sm text-slate-500 dark:text-slate-400">
                      {type.description}
                    </p>
                  </div>
                </div>
                <Toggle
                  checked={preferences[type.key]}
                  onChange={() => togglePreference(type.key)}
                />
              </div>
            ))}
          </div>

          {/* Budget Alert Threshold */}
          {preferences.low_balance && (
            <div className="mt-6 pt-6 border-t border-slate-200 dark:border-slate-700">
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                Budjetti-varoitusraja (€)
              </label>
              <p className="text-sm text-slate-500 dark:text-slate-400 mb-3">
                Ilmoita kun budjettia on käytetty tämän verran (euroissa)
              </p>
              <input
                type="number"
                min="0"
                step="100"
                value={preferences.low_balance_threshold}
                onChange={(e) => handleThresholdChange(e.target.value)}
                className="
                  w-full max-w-xs px-4 py-2
                  border border-slate-300 dark:border-slate-600 rounded-lg
                  bg-white dark:bg-slate-800
                  text-slate-900 dark:text-white
                  focus:ring-2 focus:ring-blue-500 focus:border-transparent
                "
              />
            </div>
          )}
        </FormSection>
      </Card>

      {/* Save Button */}
      <FormActions>
        <Button
          onClick={savePreferences}
          disabled={saving}
          className="min-w-[150px]"
        >
          {saving ? t('common.saving') : t('common.saveSettings')}
        </Button>
      </FormActions>
    </div>
  )
}
